/**
 * An SVG without a `viewBox` draws at its own pixel size and never scales: put
 * in a circle sized in percentages, or in a header sized by its height, it shows
 * a cropped corner of the drawing (issue #22). The logo is therefore given a
 * `viewBox` when it comes in, from the `width` and `height` it states, and an
 * SVG that states neither is refused. Browser and server run this one function,
 * so what the dialog previews is what gets stored.
 */

export type SvgSizing =
  /** Nothing to do: a `viewBox` is already there (or the text has no `<svg>` root to speak of). */
  | { status: 'unchanged' }
  /** The same SVG with a `viewBox` added on its root element, nothing else touched. */
  | { status: 'fixed'; svg: string }
  /** No `viewBox` and no usable `width` / `height`: there is nothing to scale from. */
  | { status: 'no-size' };

/** How much of the text is searched for the root element, as `detectLogoMimeType` does. */
const ROOT_SEARCH_CHARS = 4096;

/** What may precede the root element: BOM, XML declaration, comments, a doctype. */
const ROOT_START =
  /^\uFEFF?\s*(?:<\?xml[^>]*\?>\s*)?(?:<!--[\s\S]*?-->\s*)*(?:<!DOCTYPE[^[>]*(?:\[[\s\S]*?\])?\s*>\s*)?(?:<!--[\s\S]*?-->\s*)*<svg(?=[\s>/])/i;

/** CSS pixels per unit, at the 96 dpi a browser resolves absolute lengths with. */
const PX_PER_UNIT: Readonly<Record<string, number>> = {
  '': 1,
  px: 1,
  pt: 96 / 72,
  pc: 16,
  mm: 96 / 25.4,
  cm: 96 / 2.54,
  in: 96,
};

const LENGTH = /^\s*([+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?)\s*([a-z%]*)\s*$/i;

/** A `width` / `height` in CSS pixels, or null for what has no fixed size (`%`, `em`, `auto`, junk). */
function lengthInPx(value: string): number | null {
  const match = LENGTH.exec(value);
  const factor = PX_PER_UNIT[(match?.[2] ?? '').toLowerCase()];
  if (!match?.[1] || factor === undefined) return null;
  const px = Number(match[1]) * factor;
  return Number.isFinite(px) && px > 0 ? px : null;
}

/** Four decimals at most, no exponent, no trailing zeros. */
function formatNumber(value: number): string {
  return value.toFixed(4).replace(/\.?0+$/, '');
}

const ATTRIBUTE = /[\s/]*([^\s=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/y;

/** The attributes of the start tag whose name ends at `from`, with names as written (XML is case-sensitive). */
function readAttributes(text: string, from: number): Map<string, string> | null {
  const attributes = new Map<string, string>();
  let i = from;
  while (i < text.length) {
    const next = text[i];
    if (next === '>') return attributes;
    if (next !== undefined && /[\s/]/.test(next)) {
      i += 1;
      continue;
    }
    ATTRIBUTE.lastIndex = i;
    const match = ATTRIBUTE.exec(text);
    if (!match?.[1]) return null;
    attributes.set(match[1], match[2] ?? match[3] ?? match[4] ?? '');
    i = ATTRIBUTE.lastIndex;
  }
  return null;
}

/**
 * Gives an SVG without a `viewBox` the `viewBox="0 0 w h"` its `width` and
 * `height` call for (`px`, no unit, or `pt`, `pc`, `mm`, `cm`, `in` converted to
 * CSS pixels), written right after `<svg` on the root element: `width` and
 * `height` stay, so the image keeps the size and proportions it announced, and
 * the rest of the text is not touched. An SVG that has a `viewBox` is left alone,
 * whatever it says. One whose size is missing, relative (`%`, `em`) or not a
 * positive number cannot be scaled from anything: `no-size`.
 */
export function ensureSvgViewBox(svg: string): SvgSizing {
  const root = ROOT_START.exec(svg.slice(0, ROOT_SEARCH_CHARS));
  if (!root) return { status: 'unchanged' };
  const afterName = root[0].length;
  const attributes = readAttributes(svg, afterName);
  if (!attributes) return { status: 'unchanged' };
  if (attributes.has('viewBox')) return { status: 'unchanged' };

  const width = lengthInPx(attributes.get('width') ?? '');
  const height = lengthInPx(attributes.get('height') ?? '');
  if (width === null || height === null) return { status: 'no-size' };
  const w = formatNumber(width);
  const h = formatNumber(height);
  if (w === '0' || h === '0') return { status: 'no-size' };

  return {
    status: 'fixed',
    svg: `${svg.slice(0, afterName)} viewBox="0 0 ${w} ${h}"${svg.slice(afterName)}`,
  };
}
